import { Box, Skeleton } from "@mui/material";
import { Badge } from "@vilocnv/allsetra-core";
import { HiddenBadgeIcon } from "components/ui/icons";
import LocationLink from "components/ui/links/LocationLink";
import { getAddressDisplayInfo } from "../addressValidationHelpers";

/**
 * Address Render Helper for Reports
 *
 * This helper provides a consistent way to render addresses in report tables
 * with proper validation for hidden, unknown, N/A, and valid addresses.
 */

export interface RenderAddressOptions {
  latitude: number | null | undefined;
  longitude: number | null | undefined;
  resolvedAddress: string | null | undefined;
  translator: any;
  isLoading?: boolean;
  rideMode?: number;
}

/**
 * Renders an address cell with proper validation and badges
 *
 * @param options - Configuration object for rendering the address
 * @returns React element to be displayed in the table cell
 *
 * @example
 * // In a table column definition:
 * {
 *   cell: (row: any) => renderAddressCell({
 *     latitude: row.startLatitude,
 *     longitude: row.startLongitude,
 *     resolvedAddress: getResolvedAddress(row, resolvedAddresses, "start"),
 *     translator: t,
 *     isLoading: resolvedAddressLoading,
 *     rideMode: row.rideMode
 *   })
 * }
 */
export const renderAddressCell = ({
  latitude,
  longitude,
  resolvedAddress,
  translator,
  isLoading = false,
  rideMode
}: RenderAddressOptions) => {
  // Check if this is a private ride (rideMode === 2) - address should be hidden
  if (rideMode === 2) {
    return (
      <Box mx={20}>
        <Badge
          colorScheme="blank"
          icon={<HiddenBadgeIcon />}
          textWeight="light"
        >
          {translator("typography.addressIsHidden")}
        </Badge>
      </Box>
    );
  }

  // Show loading skeleton while resolving addresses
  if (isLoading) {
    return (
      <>
        <Skeleton variant="text" width={100} height={18} />
      </>
    );
  }

  // Get address display information based on coordinates and resolved address
  const addressInfo = getAddressDisplayInfo(
    latitude,
    longitude,
    resolvedAddress
  );

  // Address is hidden (coordinates are null/undefined)
  if (addressInfo.isHidden) {
    return (
      <Box mx={20}>
        <Badge
          colorScheme="blank"
          icon={<HiddenBadgeIcon />}
          textWeight="light"
        >
          {translator("typography.addressIsHidden")}
        </Badge>
      </Box>
    );
  }

  // Address is unknown (coordinates are 0,0)
  if (addressInfo.isUnknown) {
    return (
      <Box mx={20}>
        <Badge colorScheme="blank" textWeight="light">
          {translator("typography.addressUnknown")}
        </Badge>
      </Box>
    );
  }

  // Address is N/A (resolved as "N/A")
  if (addressInfo.isNA) {
    return (
      <Box mx={20}>
        <Badge colorScheme="blank" textWeight="light">
          N/A
        </Badge>
      </Box>
    );
  }

  // Valid address - show the resolved address
  if (addressInfo.shouldShow && resolvedAddress) {
    return (
      <Box
        sx={{
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "anywhere"
        }}
      >
        {resolvedAddress}
      </Box>
    );
  }

  // Fallback: If we have coordinates but no resolved address, show location link
  if (latitude && longitude) {
    return <LocationLink latitude={latitude} longitude={longitude} />;
  }

  // Final fallback
  return "N/A";
};

/**
 * Helper to get resolved address from the resolved addresses array
 *
 * @param row - The data row containing coordinates
 * @param resolvedAddresses - Array of resolved addresses
 * @param type - "start" or "end" to determine which coordinates to use
 * @returns The resolved address string or null
 */
export const getResolvedAddressForCell = (
  row: {
    startLatitude?: number;
    startLongitude?: number;
    endLatitude?: number;
    endLongitude?: number;
  },
  resolvedAddresses: Array<{
    latitude: number;
    longitude: number;
    address: string;
  }>,
  type: "start" | "end"
): string | null => {
  const latitudeKey = type === "start" ? "startLatitude" : "endLatitude";
  const longitudeKey = type === "start" ? "startLongitude" : "endLongitude";

  const latitude = row[latitudeKey as keyof typeof row];
  const longitude = row[longitudeKey as keyof typeof row];

  const foundAddress = resolvedAddresses.find(
    (item) => item.latitude === latitude && item.longitude === longitude
  );

  return foundAddress?.address || null;
};
